import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, ToastController } from '@ionic/angular';
import { EventTypePickerHandler } from '@libs/util-services';
import { MultiRentsModalComponent } from '../multi-rents-modal/multi-rents-modal.component';
import {
  EventTypeChoice,
  EventTypePickerModalComponent,
} from './event-type-picker-modal.component';

@Injectable({ providedIn: 'root' })
export class EventTypePickerService implements EventTypePickerHandler {
  private readonly modalCtrl = inject(ModalController);
  private readonly toastCtrl = inject(ToastController);
  private readonly router = inject(Router);

  /**
   * Abre o picker e dirige para o fluxo correspondente:
   *  - 'single'  → garante que o usuário está na visão Dia da agenda e
   *                instrui a tocar num horário livre da grade. (O fluxo
   *                natural é o gestor tocar no slot — sem inventar
   *                horários, porque o backend exige `idProvideCourt`.)
   *  - 'bulk'    → abre o MultiRentsModal (componente autônomo).
   *  - 'block'   → navega para /bloquear-periodo.
   *  - 'special' → navega para /bloquear-periodo?tab=unusual.
   *  - 'dayuse'  → navega para /dayuse/dates/new (marcar dias como day use).
   */
  async openPicker(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: EventTypePickerModalComponent,
      cssClass: 'event-type-picker-modal',
      backdropDismiss: true,
      showBackdrop: true,
    });
    await modal.present();
    const { data } = await modal.onWillDismiss<{ choice: EventTypeChoice }>();

    if (!data?.choice) return;

    switch (data.choice) {
      case 'single':
        await this.handleSingle();
        break;
      case 'bulk':
        await this.openMultiRentsModal();
        break;
      case 'block':
        this.router.navigate(['/bloquear-periodo']);
        break;
      case 'special':
        this.router.navigate(['/bloquear-periodo'], {
          queryParams: { tab: 'unusual' },
        });
        break;
      case 'dayuse':
        // Vai direto ao cadastro de dias: quem escolheu "Day Use" no picker da
        // agenda quer marcar um dia, não navegar por uma home de módulo.
        this.router.navigate(['/dayuse/dates/new']);
        break;
    }
  }

  private async handleSingle(): Promise<void> {
    // Se já estamos na agenda, basta dar a dica visual; senão, navega.
    const onAgenda = this.router.url.startsWith('/agenda');
    if (!onAgenda) {
      await this.router.navigate(['/agenda']);
    }
    await this.showHint('Toque num horário livre na grade para criar a reserva.', 'primary');
  }

  private async openMultiRentsModal(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: MultiRentsModalComponent,
      cssClass: 'multi-rents-modal',
      backdropDismiss: true,
      showBackdrop: true,
    });
    await modal.present();
  }

  private async showHint(message: string, color = 'medium'): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 2800,
      position: 'bottom',
      color,
    });
    await toast.present();
  }
}
